// FloatingHelper.jsx — Message flottant style Bitrix24
// Petit message qui reste ouvert jusqu'à fermeture par l'utilisateur
// S'affiche sur Desktop uniquement (≥ 1024px)
// Props: variant = 'pro' | 'particulier'
// Supporte le mode Dark/Light via ThemeContext

import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

export default function FloatingHelper({ variant = 'pro' }) {
  const [isVisible, setIsVisible] = useState(true);
  const [isDesktop, setIsDesktop] = useState(false);
  
  // Utiliser le context de thème
  let isDarkMode = false;
  try {
    const theme = useTheme();
    isDarkMode = theme?.isDarkMode || false;
  } catch (e) {
    // Si pas dans un ThemeProvider, fallback sur localStorage
    isDarkMode = localStorage.getItem('abracadabati-theme') === 'dark';
  }

  useEffect(() => {
    const checkDesktop = () => setIsDesktop(window.innerWidth >= 1024);
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  // Vérifier si déjà fermé pour cette session
  useEffect(() => {
    const dismissed = sessionStorage.getItem(`floatingHelper_${variant}_dismissed`);
    if (dismissed) {
      setIsVisible(false);
    }
  }, [variant]);

  const handleClose = () => {
    setIsVisible(false);
    sessionStorage.setItem(`floatingHelper_${variant}_dismissed`, 'true');
  };

  // Ne pas afficher sur mobile/tablette
  if (!isDesktop || !isVisible) {
    return null;
  }

  // Contenu selon le variant
  const content = variant === 'pro' 
    ? {
        title: "Besoin d'aide pour démarrer ?",
        text: "Notre équipe est là pour t'accompagner dans la prise en main de ton espace professionnel.",
        avatar: "👨‍💼",
        accentColor: "#E8650A",
      }
    : {
        title: "Bienvenue dans ton espace !",
        text: "Découvre comment trouver les meilleurs artisans et gérer tes projets facilement.",
        avatar: "👋",
        accentColor: "#E8650A",
      };

  // Styles adaptés au mode dark/light
  const styles = isDarkMode ? {
    bg: 'bg-[#1E3A5F]',
    border: 'border-[#2d5a8a]',
    title: 'text-white',
    text: 'text-gray-300',
    closeBtn: 'text-gray-400 hover:text-white hover:bg-white/10',
    indicator: 'text-gray-400',
    indicatorBorder: 'border-[#2d5a8a]',
    shadow: '0 10px 40px rgba(0,0,0,0.3), 0 4px 12px rgba(0,0,0,0.2)',
    arrowBg: '#1E3A5F',
    arrowBorder: '#2d5a8a',
  } : {
    bg: 'bg-white',
    border: 'border-gray-100',
    title: 'text-gray-900',
    text: 'text-gray-600',
    closeBtn: 'text-gray-400 hover:text-gray-600 hover:bg-gray-100',
    indicator: 'text-gray-500',
    indicatorBorder: 'border-gray-100',
    shadow: '0 10px 40px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.08)',
    arrowBg: 'white',
    arrowBorder: '#f3f4f6',
  };

  return (
    <div
      className="fixed z-[999] animate-in slide-in-from-right-5 duration-500"
      style={{
        bottom: '100px',
        right: '24px',
        maxWidth: '320px',
      }}
    >
      {/* Bulle de message */}
      <div 
        className={`relative ${styles.bg} rounded-2xl shadow-xl border ${styles.border} p-4`}
        style={{ boxShadow: styles.shadow }}
      >
        {/* Bouton fermer */}
        <button
          onClick={handleClose}
          className={`absolute top-2 right-2 p-1.5 ${styles.closeBtn} rounded-full transition-colors`}
          title="Fermer"
        >
          <X size={16} />
        </button>

        {/* Contenu */}
        <div className="flex gap-3 pr-6">
          {/* Avatar */}
          <div 
            className="w-12 h-12 rounded-full flex items-center justify-center text-2xl flex-shrink-0"
            style={{ 
              background: isDarkMode 
                ? `linear-gradient(135deg, ${content.accentColor}44, ${content.accentColor}22)`
                : `linear-gradient(135deg, ${content.accentColor}22, ${content.accentColor}11)`,
              border: `2px solid ${content.accentColor}${isDarkMode ? '66' : '33'}`,
            }}
          >
            {content.avatar}
          </div>

          {/* Texte */}
          <div className="flex-1 min-w-0">
            <h4 
              className={`font-semibold ${styles.title} text-sm mb-1`}
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              {content.title}
            </h4>
            <p 
              className={`${styles.text} text-xs leading-relaxed`}
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              {content.text}
            </p>
          </div>
        </div>

        {/* Indicateur de connexion */}
        <div className={`flex items-center gap-2 mt-3 pt-3 border-t ${styles.indicatorBorder}`}>
          <span 
            className="w-2 h-2 rounded-full"
            style={{ 
              background: '#22C55E',
              boxShadow: '0 0 6px rgba(34, 197, 94, 0.5)',
            }}
          />
          <span className={`text-xs ${styles.indicator}`}>
            Version bêta — Ouverture le 1er juillet 2026
          </span>
        </div>

        {/* Flèche pointant vers le bas/droite */}
        <div 
          className="absolute -bottom-2 right-8 w-4 h-4 rotate-45"
          style={{ 
            background: styles.arrowBg,
            borderBottom: `1px solid ${styles.arrowBorder}`,
            borderRight: `1px solid ${styles.arrowBorder}`,
            boxShadow: isDarkMode ? 'none' : '2px 2px 4px rgba(0,0,0,0.05)',
          }}
        />
      </div>
    </div>
  );
}
